/**
 * Smart Ranking Algorithm with Greedy Transitivity Optimization
 *
 * Instead of following a fixed merge-sort structure, this algorithm:
 * 1. Tracks all unknown pairwise relationships
 * 2. For each potential comparison, scores it by transitivity benefit
 * 3. Always asks the comparison that would give us the most inferred relationships
 * 4. Uses topological sort at the end to produce the final order
 *
 * For n tasks, the minimum comparisons needed is n-1 (a chain: A > B > C > ...)
 * This algorithm aims to achieve close to that minimum by maximizing transitivity.
 */

import {
  PreferenceGraph,
  isMoreUrgent,
  addComparison,
  topologicalSort,
} from './preferenceGraph';

export interface SmartRankingState {
  // All tasks being sorted (task IDs)
  allTasks: string[];
  // Total comparisons made (asked user)
  comparisonsMade: number;
  // Comparisons skipped due to transitivity
  comparisonsSkipped: number;
  // Estimated total comparisons (n-1 minimum)
  estimatedTotal: number;
  // Is the sort complete?
  isComplete: boolean;
  // Final sorted order (populated when complete)
  finalOrder: string[];
  // Pairs that user skipped (tie) - stored as "taskA|taskB" with IDs sorted
  skippedPairs: Set<string>;
}

/**
 * Initialize smart ranking state for a set of tasks.
 */
export function initializeSmartRanking(taskIds: string[]): SmartRankingState {
  // For n tasks, comparisons needed ranges from:
  // - Minimum: n-1 (perfect chain, maximum transitivity)
  // - Average: ~n*log2(n)/2 (typical case with some transitivity)
  // - Maximum: n*(n-1)/2 (no transitivity at all)
  // We use a realistic estimate closer to average case
  const n = taskIds.length;
  const estimatedTotal = n <= 1 ? 0 : Math.ceil(n * Math.log2(Math.max(2, n)) * 0.6);

  return {
    allTasks: [...taskIds],
    comparisonsMade: 0,
    comparisonsSkipped: 0,
    estimatedTotal,
    isComplete: n <= 1,
    finalOrder: n <= 1 ? [...taskIds] : [],
    skippedPairs: new Set<string>(),
  };
}

/**
 * Count how many NEW relationships we'd learn if we knew the result of A vs B.
 * This includes the direct relationship plus all transitive inferences.
 *
 * IMPORTANT: We heavily favor comparisons that EXTEND CHAINS rather than create FANS.
 * - Fan pattern (A>B, A>C, A>D): gives 0 transitivity
 * - Chain pattern (A>B, B>C, C>D): gives maximum transitivity (A>C, A>D, B>D all inferred)
 */
function countTransitivityBenefit(
  graph: PreferenceGraph,
  taskA: string,
  taskB: string,
  allTasks: string[]
): number {
  // If we already know A vs B, benefit is 0
  if (isMoreUrgent(graph, taskA, taskB) !== null) {
    return 0;
  }

  // Count tasks that A is known to beat (A > X)
  const aBeats = new Set<string>();
  // Count tasks that beat A (X > A)
  const beatsA = new Set<string>();
  // Count tasks that B is known to beat (B > X)
  const bBeats = new Set<string>();
  // Count tasks that beat B (X > B)
  const beatsB = new Set<string>();

  for (const task of allTasks) {
    if (task === taskA || task === taskB) continue;

    const aVsTask = isMoreUrgent(graph, taskA, task);
    if (aVsTask === true) aBeats.add(task);
    else if (aVsTask === false) beatsA.add(task);

    const bVsTask = isMoreUrgent(graph, taskB, task);
    if (bVsTask === true) bBeats.add(task);
    else if (bVsTask === false) beatsB.add(task);
  }

  // Base benefit: relationships we'd learn directly
  // If A > B: beatsA items now also beat B, and A now beats bBeats items
  const benefitIfAWins = 1 + beatsA.size + bBeats.size;
  // If B > A: beatsB items now also beat A, and B now beats aBeats items
  const benefitIfBWins = 1 + beatsB.size + aBeats.size;
  let score = (benefitIfAWins + benefitIfBWins) / 2;

  // CHAIN-BUILDING BONUS: Strongly prefer comparisons that extend existing chains
  // This is the key insight: we want to compare items that are already "connected"
  // to the graph, so we create chains (A>B>C) instead of fans (A>B, A>C, A>D)
  const aConnectedness = aBeats.size + beatsA.size;
  const bConnectedness = bBeats.size + beatsB.size;

  if (aConnectedness > 0 && bConnectedness > 0) {
    // BEST: Both items are already connected to the graph
    // This comparison will definitely create transitive chains!
    // Example: if X>A and B>Y, learning A>B gives us X>A>B>Y (chain of 4)
    score += 100 + (aConnectedness * bConnectedness);
  } else if (aConnectedness > 0 || bConnectedness > 0) {
    // GOOD: One item is connected - this extends an existing chain
    // Example: if X>A, learning A>B gives us X>A>B
    score += 50 + Math.max(aConnectedness, bConnectedness);
  }
  // If neither is connected, no bonus - these are "bootstrap" comparisons

  // Small random factor to break ties (avoids always picking first pair)
  score += Math.random() * 0.5;

  return score;
}

/**
 * Find all pairs where we don't know the relationship.
 * Excludes pairs that were explicitly skipped by the user.
 */
function findUnknownPairs(
  graph: PreferenceGraph,
  allTasks: string[],
  skippedPairs?: Set<string>
): Array<[string, string]> {
  const unknownPairs: Array<[string, string]> = [];

  for (let i = 0; i < allTasks.length; i++) {
    for (let j = i + 1; j < allTasks.length; j++) {
      const taskA = allTasks[i];
      const taskB = allTasks[j];
      // Skip if we already know the relationship
      if (isMoreUrgent(graph, taskA, taskB) !== null) {
        continue;
      }
      // Skip if user explicitly skipped this pair
      if (skippedPairs && skippedPairs.has(makePairKey(taskA, taskB))) {
        continue;
      }
      unknownPairs.push([taskA, taskB]);
    }
  }

  return unknownPairs;
}

/**
 * Result of getNextSmartComparison
 */
export type SmartComparisonResult =
  | { type: 'comparison'; taskA: string; taskB: string; state: SmartRankingState }
  | { type: 'complete'; state: SmartRankingState };

/**
 * Get the next comparison to ask, optimized for maximum transitivity benefit.
 * Returns null only if there are no tasks to rank.
 */
export function getNextSmartComparison(
  state: SmartRankingState,
  graph: PreferenceGraph
): SmartComparisonResult | null {
  if (state.allTasks.length <= 1) {
    return {
      type: 'complete',
      state: {
        ...state,
        isComplete: true,
        finalOrder: [...state.allTasks],
      },
    };
  }

  // Find all unknown pairs (excluding user-skipped pairs)
  const unknownPairs = findUnknownPairs(graph, state.allTasks, state.skippedPairs);

  // If no unknown pairs, we're done!
  if (unknownPairs.length === 0) {
    // Use topological sort to get final order
    const finalOrder = topologicalSort(graph, state.allTasks);
    return {
      type: 'complete',
      state: {
        ...state,
        isComplete: true,
        finalOrder,
      },
    };
  }

  // Score each unknown pair by transitivity benefit
  let bestPair: [string, string] = unknownPairs[0];
  let bestScore = -1;

  for (const pair of unknownPairs) {
    const score = countTransitivityBenefit(graph, pair[0], pair[1], state.allTasks);
    if (score > bestScore) {
      bestScore = score;
      bestPair = pair;
    }
  }

  // Return the best comparison to ask
  return {
    type: 'comparison',
    taskA: bestPair[0],
    taskB: bestPair[1],
    state,
  };
}

/**
 * Process a user's comparison choice.
 */
export function processSmartChoice(
  state: SmartRankingState,
  taskA: string,
  taskB: string,
  choice: 'left' | 'right',
  graph: PreferenceGraph
): SmartRankingState {
  // Record the comparison in the preference graph
  if (choice === 'left') {
    addComparison(graph, taskA, taskB);
  } else {
    addComparison(graph, taskB, taskA);
  }

  return {
    ...state,
    comparisonsMade: state.comparisonsMade + 1,
  };
}

/**
 * Process "too tough" - treat as roughly equal, pick left arbitrarily.
 * Don't record in preference graph to avoid incorrect transitive inferences.
 */
export function processSmartTooTough(
  state: SmartRankingState,
  taskA: string,
  taskB: string,
  graph: PreferenceGraph
): SmartRankingState {
  // Pick left arbitrarily without recording preference
  // This avoids creating potentially wrong edges in the graph
  addComparison(graph, taskA, taskB);

  return {
    ...state,
    comparisonsMade: state.comparisonsMade + 1,
  };
}

/**
 * Create a consistent key for a pair of tasks (alphabetically sorted).
 */
function makePairKey(taskA: string, taskB: string): string {
  return taskA < taskB ? `${taskA}|${taskB}` : `${taskB}|${taskA}`;
}

/**
 * Process "skip" - move to next comparison without recording preference.
 * Records the pair as skipped so we don't ask about it again.
 */
export function processSmartSkip(
  state: SmartRankingState,
  taskA: string,
  taskB: string
): SmartRankingState {
  const newSkippedPairs = new Set(state.skippedPairs);
  newSkippedPairs.add(makePairKey(taskA, taskB));

  return {
    ...state,
    comparisonsMade: state.comparisonsMade + 1,
    skippedPairs: newSkippedPairs,
  };
}

/**
 * Get progress info for UI display.
 */
export function getSmartRankingProgress(
  state: SmartRankingState,
  graph: PreferenceGraph
): {
  currentComparison: number;
  estimatedTotal: number;
  percentComplete: number;
  comparisonsSkipped: number;
} {
  // Find unknown pairs excluding user-skipped ones
  const unknownPairs = findUnknownPairs(graph, state.allTasks, state.skippedPairs);
  const totalPairs = (state.allTasks.length * (state.allTasks.length - 1)) / 2;
  // Known pairs = total - unknown - user-skipped
  const knownPairs = totalPairs - unknownPairs.length - state.skippedPairs.size;

  // Estimate remaining: at least unknownPairs.length more comparisons needed
  // But transitivity might reduce this
  const estimatedRemaining = Math.max(0, unknownPairs.length);
  const estimatedTotal = state.comparisonsMade + estimatedRemaining;

  // Calculate skipped as the difference between direct comparisons and total pairs known
  const comparisonsSkipped = Math.max(0, knownPairs - state.comparisonsMade);

  return {
    currentComparison: state.comparisonsMade + 1,
    estimatedTotal: Math.max(estimatedTotal, state.allTasks.length - 1),
    percentComplete: totalPairs > 0 ? (knownPairs / totalPairs) * 100 : 100,
    comparisonsSkipped,
  };
}
